import { useState } from 'react';
import { LabelTemplate, IsomPart, PipeType, PlacedBlock, SocketConnection, TextAnnotation, DrawingFolder, Drawing } from '@/app/types/cad';
import { BlockLibraryPanel } from './BlockLibraryPanel';
import { LabelLibraryPanel } from './LabelLibraryPanel';
import { DrawingBrowser } from './DrawingBrowser';
import { Printer, Package, FolderTree, Tag } from 'lucide-react';
import { ScrollArea } from '@/app/components/ui/scroll-area';
import { useLanguage } from '@/app/contexts/LanguageContext';

export type SidebarTab = 'bauteile' | 'browser' | 'beschriftungen';

interface BlockSystemSidebarProps {
  // Navigation control
  activeTab?: SidebarTab;
  onTabChange?: (tab: SidebarTab) => void;

  // Bauteile
  onBlockSelect: (block: IsomPart) => void;
  
  // Leitungen (Pipe Types)
  pipeTypes: PipeType[];
  
  // Beschriftungen (Labels)
  labelTemplates: LabelTemplate[];
  onLabelTemplateAdd: (template: LabelTemplate) => void;
  onLabelTemplateUpdate: (template: LabelTemplate) => void;
  onLabelTemplateDelete: (templateId: string) => void;
  onLabelTemplateSelect: (template: LabelTemplate) => void;
  selectedLabelTemplateId?: string;
  
  // Projekt-Daten für Attribut-Extraktion
  blocks?: PlacedBlock[];
  connections?: SocketConnection[];
  textAnnotations?: TextAnnotation[];
  
  // Drawing Browser
  rootFolder: DrawingFolder;
  activeDrawingId?: string;
  activePlanId?: string; // New
  onDrawingSelect: (drawingId: string) => void;
  onPlanSelect?: (planId: string, drawingId: string) => void; // New
  onDrawingCreate: (parentFolderId: string, name: string) => void;
  onDrawingRename: (drawingId: string, newName: string) => void;
  onDrawingDelete: (drawingId: string) => void;
  onDrawingDuplicate: (drawingId: string) => void;
  onFolderCreate: (parentFolderId: string, name: string) => void;
  onFolderRename: (folderId: string, newName: string) => void;
  onFolderDelete: (folderId: string) => void;
  onFolderToggle: (folderId: string) => void;
  onItemMove?: (itemId: string, targetFolderId: string) => void;
  onPlanCreate?: (drawingId: string) => void; // New
  onPlanDelete?: (planId: string, drawingId: string) => void; // New
}

export function BlockSystemSidebar({
  onBlockSelect,
  pipeTypes,
  labelTemplates,
  onLabelTemplateAdd,
  onLabelTemplateUpdate,
  onLabelTemplateDelete,
  onLabelTemplateSelect,
  selectedLabelTemplateId,
  blocks = [],
  connections = [],
  textAnnotations = [],
  rootFolder,
  activeDrawingId,
  activePlanId,
  onDrawingSelect,
  onPlanSelect,
  onDrawingCreate,
  onDrawingRename,
  onDrawingDelete,
  onDrawingDuplicate,
  onFolderCreate,
  onFolderRename,
  onFolderDelete,
  onFolderToggle,
  onItemMove,
  onPlanCreate,
  onPlanDelete,
  activeTab: propsActiveTab,
  onTabChange
}: BlockSystemSidebarProps) {
  const { t } = useLanguage();
  const [internalActiveTab, setInternalActiveTab] = useState<SidebarTab>('browser');
  const activeTab = propsActiveTab !== undefined ? propsActiveTab : internalActiveTab;

  const handleTabChange = (tab: SidebarTab) => {
    if (onTabChange) {
      onTabChange(tab);
    } else {
      setInternalActiveTab(tab);
    }
  };

  const tabs: { id: SidebarTab; label: string; icon: React.ReactNode }[] = [
    { id: 'browser', label: t('sidebar.tabs.browser'), icon: <FolderTree className="w-4 h-4" /> },
    { id: 'bauteile', label: t('sidebar.tabs.parts'), icon: <Package className="w-4 h-4" /> },
    { id: 'beschriftungen', label: t('sidebar.tabs.labels'), icon: <Tag className="w-4 h-4" /> },
    // { id: 'pläne', label: 'Pläne', icon: <Printer className="w-4 h-4" /> }, // REMOVED
  ];

  return (
    <div className="w-full h-full flex flex-col" style={{
      backgroundColor: 'var(--md-surface-container)',
      borderColor: 'var(--md-outline-variant)'
    }}>
      {/* Tabs */}
      <div className="flex border-b" style={{ borderColor: 'var(--md-outline-variant)' }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            className={`
              flex-1 px-3 py-2.5 text-xs font-medium transition-colors
              flex items-center justify-center gap-1.5
              ${activeTab === tab.id
                ? 'border-b-2'
                : 'opacity-60 hover:opacity-100'
              }
            `}
            style={activeTab === tab.id ? {
              color: 'var(--md-primary)',
              borderBottomColor: 'var(--md-primary)'
            } : {
              color: 'var(--md-on-surface-variant)'
            }}
          >
            {tab.icon}
            <span className="hidden lg:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'bauteile' && (
          <BlockLibraryPanel onBlockSelect={onBlockSelect} />
        )}

        {activeTab === 'browser' && (
          <DrawingBrowser
            rootFolder={rootFolder}
            activeDrawingId={activeDrawingId}
            activePlanId={activePlanId}
            onDrawingSelect={onDrawingSelect}
            onPlanSelect={onPlanSelect}
            onDrawingCreate={onDrawingCreate}
            onDrawingRename={onDrawingRename}
            onDrawingDelete={onDrawingDelete}
            onDrawingDuplicate={onDrawingDuplicate}
            onFolderCreate={onFolderCreate}
            onFolderRename={onFolderRename}
            onFolderDelete={onFolderDelete}
            onFolderToggle={onFolderToggle}
            onItemMove={onItemMove}
            onPlanCreate={onPlanCreate}
            onPlanDelete={onPlanDelete}
          />
        )}

        {activeTab === 'beschriftungen' && (
          <LabelLibraryPanel
            templates={labelTemplates}
            onTemplateAdd={onLabelTemplateAdd}
            onTemplateUpdate={onLabelTemplateUpdate}
            onTemplateDelete={onLabelTemplateDelete}
            onTemplateSelect={onLabelTemplateSelect}
            selectedTemplateId={selectedLabelTemplateId}
            blocks={blocks}
            connections={connections}
            textAnnotations={textAnnotations}
          />
        )}
      </div>
    </div>
  );
}

// Placeholder für Leitungstypen-Panel (kann später erweitert werden)
interface PipeTypesPanelProps {
  pipeTypes: PipeType[];
}

function PipeTypesPanel({ pipeTypes }: PipeTypesPanelProps) {
  const { t } = useLanguage();

  return (
    <div className="h-full flex flex-col p-4">
      <h3 className="font-semibold mb-2">Leitungstypen</h3>
      <div className="text-sm text-gray-500">
        {pipeTypes.length > 0 ? (
          <div className="space-y-2">
            {pipeTypes.map(pipe => (
              <div key={pipe.id} className="p-2 border rounded">
                <div className="font-medium">{pipe.name}</div>
                <div className="text-xs text-gray-500">DN {pipe.dn}</div>
              </div>
            ))}
          </div>
        ) : (
          t('sidebar.pipe.noTypes')
        )}
      </div>
    </div>
  );
}